// Environment configuration
// Helper to get backend URL - use environment variable or derive from current location
const getBackendUrl = (): string => {
  // In browser, try to use window location to determine backend URL
  if (typeof window !== 'undefined') {
    // If we're in a test environment or have a specific backend URL set, use it
    const testBackendUrl = (window as any).__TEST_BACKEND_URL__;
    if (testBackendUrl) {
      return testBackendUrl;
    }
    
    // In development, try to derive backend URL from current location
    // For workspace setups, backend is typically on port + 1 from frontend
    if (import.meta.env.DEV) {
      const currentPort = window.location.port;
      const protocol = window.location.protocol;
      
      if (currentPort) {
        const portNum = parseInt(currentPort, 10);
        // Check if this looks like a workspace port (3020, 3010, etc.)
        if (portNum >= 3000 && portNum < 3100 && portNum % 10 === 0) {
          const backendPort = portNum + 1;
          // Use the same protocol as the current page (http or https)
          const derivedUrl = `${protocol}//${window.location.hostname}:${backendPort}`;
          return derivedUrl;
        }
      }
      
      // If port doesn't match workspace pattern, still use current protocol
      const protocolAwareUrl = `${protocol}//${window.location.hostname}:3001`;
      return protocolAwareUrl;
    }
  }
  
  // Final fallback - use current protocol if in browser, otherwise default to HTTP
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    return `${protocol}//localhost:3001`;
  }
  return 'http://localhost:3001';
};

// Helper to get WebSocket URL - use same origin in dev to go through Vite proxy
const getWebSocketUrl = (): string => {
  // Return cached value if available
  if (cachedWebSocketUrl !== null) {
    return cachedWebSocketUrl;
  }
  
  // Get current protocol
  const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
  const expectedScheme = currentProtocol === 'https:' ? 'https' : 'http';
  
  // If explicitly set via environment variable, use it (check both VITE_WS_URL and VITE_WEBSOCKET_URL)
  let wsUrl = import.meta.env.VITE_WS_URL;
  if (!wsUrl) {
    wsUrl = import.meta.env.VITE_WEBSOCKET_URL;
  }
  
  if (wsUrl) {
    // Fix protocol mismatch if needed
    const envScheme = wsUrl.startsWith('https://') ? 'https' : 'http';
    if (envScheme !== expectedScheme && typeof window !== 'undefined') {
      if (!configInitialized || import.meta.env.DEV) {
        console.warn(`[Config] WebSocket protocol mismatch: env has ${envScheme}:// but page is ${expectedScheme}://, fixing URL`);
      }
      wsUrl = wsUrl.replace(/^https?:\/\//, `${expectedScheme}://`);
    }
    
    // If URL contains localhost and we're in browser, replace with current hostname
    // This allows the app to work when accessed from phone/network IP
    if (typeof window !== 'undefined' && wsUrl.includes('localhost')) {
      const currentHostname = window.location.hostname;
      // Only replace if we're not already on localhost (to avoid breaking local development)
      if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
        if (!configInitialized || import.meta.env.DEV) {
          console.log(`[Config] Replacing localhost in WebSocket URL with current hostname: ${currentHostname}`);
        }
        wsUrl = wsUrl.replace(/localhost/g, currentHostname);
      }
    }
    
    cachedWebSocketUrl = wsUrl;
    return wsUrl;
  }
  
  // In development mode, use same origin to go through Vite proxy
  // The Vite proxy is configured to forward /socket.io to the backend
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    // Use same origin - Vite proxy will handle forwarding to correct backend port
    cachedWebSocketUrl = window.location.origin;
    return cachedWebSocketUrl;
  }
  
  // In production or if not in dev, use absolute URL (which will also replace localhost)
  const backendUrl = getApiBaseUrl();
  cachedWebSocketUrl = backendUrl;
  return backendUrl;
};

// Cache for computed config values to avoid repeated calculations and logging
let cachedApiBaseUrl: string | null = null;
let cachedWebSocketUrl: string | null = null;
let configInitialized = false;

// Helper to get API base URL - prioritize environment variable, but fix protocol mismatch
const getApiBaseUrl = (): string => {
  // Return cached value if available
  if (cachedApiBaseUrl !== null) {
    return cachedApiBaseUrl;
  }
  
  // Get current protocol
  const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
  const expectedScheme = currentProtocol === 'https:' ? 'https' : 'http';
  
  // Check environment variable
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  
  // Only log during initialization or in development mode
  if (!configInitialized || import.meta.env.DEV) {
    if (!configInitialized) {
      console.log('[Config] Initializing API base URL...');
    }
  }
  
  if (envUrl && typeof envUrl === 'string' && envUrl.trim() !== '') {
    let finalUrl = envUrl;
    
    // If environment URL scheme doesn't match current protocol, fix it
    const envScheme = envUrl.startsWith('https://') ? 'https' : 'http';
    if (envScheme !== expectedScheme && typeof window !== 'undefined') {
      if (!configInitialized || import.meta.env.DEV) {
        console.warn(`[Config] Protocol mismatch: env has ${envScheme}:// but page is ${expectedScheme}://, fixing URL`);
      }
      finalUrl = finalUrl.replace(/^https?:\/\//, `${expectedScheme}://`);
    }
    
    // If URL contains localhost and we're in browser, replace with current hostname
    // This allows the app to work when accessed from phone/network IP
    if (typeof window !== 'undefined' && finalUrl.includes('localhost')) {
      const currentHostname = window.location.hostname;
      // Only replace if we're not already on localhost (to avoid breaking local development)
      if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
        if (!configInitialized || import.meta.env.DEV) {
          console.log(`[Config] Replacing localhost with current hostname: ${currentHostname}`);
        }
        finalUrl = finalUrl.replace(/localhost/g, currentHostname);
      }
    }
    
    if (!configInitialized || import.meta.env.DEV) {
      console.log('[Config] Using environment variable for API base URL:', finalUrl);
    }
    
    cachedApiBaseUrl = finalUrl;
    return finalUrl;
  }
  
  // Fallback to protocol-aware URL
  const fallbackUrl = getBackendUrl();
  if (!configInitialized || import.meta.env.DEV) {
    console.log('[Config] Environment variable not available, using fallback for API base URL:', fallbackUrl);
  }
  cachedApiBaseUrl = fallbackUrl;
  return fallbackUrl;
};

export const config = {
  // API Configuration - use getter function to ensure it's evaluated at runtime
  get apiBaseUrl() {
    const url = getApiBaseUrl();
    if (!configInitialized) {
      configInitialized = true;
    }
    return url;
  },
  get websocketUrl() {
    const url = getWebSocketUrl();
    if (!configInitialized) {
      configInitialized = true;
    }
    return url;
  },
  
  // App Configuration
  appName: import.meta.env.VITE_APP_NAME || 'CoopAdventureEngine',
  appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
  appDescription: import.meta.env.VITE_APP_DESCRIPTION || 'Cooperative Adventure Game Engine',
  
  // Development Configuration
  isDev: import.meta.env.DEV,
  enableDevtools: import.meta.env.VITE_ENABLE_DEVTOOLS === 'true',
  
  // PWA Configuration
  pwaEnabled: import.meta.env.VITE_PWA_ENABLED !== 'false',
  pwaUpdateCheckInterval: parseInt(import.meta.env.VITE_PWA_UPDATE_CHECK_INTERVAL || '60000'),
  
  // Game Configuration
  maxPlayersPerSession: parseInt(import.meta.env.VITE_MAX_PLAYERS_PER_SESSION || '6'),
  sessionTimeout: parseInt(import.meta.env.VITE_SESSION_TIMEOUT || '3600000'),
  reconnectAttempts: parseInt(import.meta.env.VITE_RECONNECT_ATTEMPTS || '5'),
  reconnectDelay: parseInt(import.meta.env.VITE_RECONNECT_DELAY || '3000'),
  
  // Feature Flags
  features: {
    offlineMode: import.meta.env.VITE_ENABLE_OFFLINE_MODE === 'true',
    pushNotifications: import.meta.env.VITE_ENABLE_PUSH_NOTIFICATIONS === 'true',
    analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  },
};

export default config;

// Export other config modules
export { imageServerConfig } from './imageServer';
export { getDefaultGradient, buildGradient } from './theme';